import React, { useState, useEffect, useRef } from "react";
import {
  Plus,
  Settings,
  Copy,
  CheckCircle,
  Edit3,
  Activity,
  Terminal,
  Trash,
  Search,
  Grid3X3,
  List,
  ChevronLeft,
  ChevronRight,
  Zap,
  Cog,
  X,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { MetricCard, MetricCardGrid } from "../../widgets/MetricCard";
import Loader from "../../widgets/Loader";
import MCPConfigForm from "../../components/mcpConfig/MCPConfigForm";
import ConfirmationDialog from "../../widgets/ConfirmationModal";
import Can from "../../components/auth/Can";
import mcpConfigService, {
  MCPConfig as MCPConfigType,
  MCPConfigListParams,
  MCPConfigStats,
  CreateMCPConfigData,
  UpdateMCPConfigData,
  ActivateToolsData,
} from "../../services/mcpConfig.service";
import ToolsManagementModal from "../../components/mcpConfig/ToolsManagementModal";
import Tooltip from "../../widgets/ToolTip";

// Grid View Component
const GridView: React.FC<{
  configs: MCPConfigType[];
  onEdit: (config: MCPConfigType) => void;
  onDelete: (config: MCPConfigType) => void;
  onManageTools: (config: MCPConfigType) => void;
}> = ({ configs, onEdit, onDelete, onManageTools }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
    {configs.map((config) => (
      <MCPConfigCard
        key={config._id}
        config={config}
        onEdit={onEdit}
        onDelete={onDelete}
        onManageTools={onManageTools}
      />
    ))}
  </div>
);

// List View Component
const ListView: React.FC<{
  configs: MCPConfigType[];
  onEdit: (config: MCPConfigType) => void;
  onDelete: (config: MCPConfigType) => void;
  onManageTools: (config: MCPConfigType) => void;
}> = ({ configs, onEdit, onDelete, onManageTools }) => {
  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid Date";
    }
  };

  const getToolCount = (config: MCPConfigType) => {
    if (!config.tools || !Array.isArray(config.tools)) return 0;
    return config.tools.length;
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Configuration
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tools
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Last Sync
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created At
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {configs.map((config) => (
              <tr key={config._id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-100 to-blue-100 rounded-full flex items-center justify-center mr-3">
                      <Terminal className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {config.mcp_name}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      !config.is_deleted
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {!config.is_deleted ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {getToolCount(config)} tools
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {config.last_sync_at
                    ? formatDate(config.last_sync_at)
                    : "Never"}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatDate(config.createdAt)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end gap-2">
                    <Tooltip content="Manage tools">
                    <button
                      onClick={() => onManageTools(config)}
                      className="text-purple-600 hover:text-purple-900 p-1 rounded"
                      type="button"
                    >
                      <Cog className="w-4 h-4" />
                    </button>
                    </Tooltip>
                    <Can permission="integrations:edit">
                    <Tooltip content="Edit">
                    <button
                      onClick={() => onEdit(config)}
                      className="text-blue-600 hover:text-blue-900 p-1 rounded"
                      type="button"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                    </Tooltip>
                    </Can>
                    <Can permission="integrations:delete">
                    <Tooltip content="Delete">
                    <button
                      onClick={() => onDelete(config)}
                      className="text-red-600 hover:text-red-900 p-1 rounded"
                      type="button"
                    >
                      <Trash className="w-4 h-4" />
                    </button>
                    </Tooltip>
                    </Can>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const MCPConfigCard: React.FC<{
  config: MCPConfigType;
  onEdit: (config: MCPConfigType) => void;
  onDelete: (config: MCPConfigType) => void;
  onManageTools: (config: MCPConfigType) => void;
}> = ({ config, onEdit, onDelete, onManageTools }) => {
  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid Date";
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const [copied, setCopied] = useState(false);

  const getToolCount = (config: MCPConfigType) => {
    if (!config.tools || !Array.isArray(config.tools)) return 0;
    return config.tools.length;
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow duration-200">
      {/* Config Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-100 to-blue-100 rounded-full flex items-center justify-center">
              <Terminal className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-sm">
                {config.mcp_name}
              </h3>
              <p className="text-xs text-gray-500">
                {!config.is_deleted ? "Active" : "Inactive"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {!config.is_deleted ? (
              <CheckCircle className="w-5 h-5 text-green-500" />
            ) : (
              <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Config Details */}
      <div className="p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Cog className="w-4 h-4" />
          <span>{getToolCount(config)} tools available</span>
        </div>

        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Zap className="w-4 h-4" />
          <span className="truncate">
            {config.last_sync_at
              ? `Synced ${formatDate(config.last_sync_at)}`
              : "Never synced"}
          </span>
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
          <span className="text-xs text-gray-500">
            {formatDate(config.createdAt)}
          </span>
          <div className="flex items-center gap-2">
            <Tooltip content={copied ? "Copied!" : "Copy"}>
            <button
              onClick={() => copyToClipboard(config.mcp_connection_npx_command)}
              className={`p-1 rounded transition-colors ${
                copied
                  ? "text-green-600 hover:text-green-900"
                  : "text-gray-400 hover:text-gray-600"
              }`}
              type="button"
            >
              {copied ? (
                <CheckCircle className="w-4 h-4" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </button>
            </Tooltip>
            <Tooltip content="Manage tools">
            <button
              onClick={() => onManageTools(config)}
              className="text-purple-600 hover:text-purple-900 p-1 rounded"
             type="button"
            >
              <Cog className="w-4 h-4" />
            </button>
            </Tooltip>
            <Can permission="integrations:edit">
            <Tooltip content="Edit">
            <button
              onClick={() => onEdit(config)}
              className="text-blue-600 hover:text-blue-900 p-1 rounded"
              type="button"
            >
              <Edit3 className="w-4 h-4" />
            </button>
            </Tooltip>
            </Can>
            <Can permission="integrations:delete">
            <Tooltip content="Delete">
            <button
              onClick={() => onDelete(config)}
              className="text-red-600 hover:text-red-900 p-1 rounded"
              type="button"
            >
              <Trash className="w-4 h-4" />
            </button>
            </Tooltip>
            </Can>
          </div>
        </div>
      </div>
    </div>
  );
};

const MCPEndpoints: React.FC = () => {
  // State management
  const [configs, setConfigs] = useState<MCPConfigType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingConfigs, setIsLoadingConfigs] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(12);
  const [searchFilters, setSearchFilters] = useState({
    mcp_name: "",
  });
  const [stats, setStats] = useState<MCPConfigStats>({
    total_configs: 0,
    active_configs: 0,
    configs_with_tools: 0,
  });
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  // Form modal state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<MCPConfigType | null>(
    null
  );

  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [configToDelete, setConfigToDelete] = useState<MCPConfigType | null>(
    null
  );

  // Tools management state
  const [showToolsModal, setShowToolsModal] = useState(false);
  const [selectedConfigForTools, setSelectedConfigForTools] = useState<MCPConfigType | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isActivatingTools, setIsActivatingTools] = useState(false);

  const isInitialMount = useRef(true);

  // Fetch data on mount and filter changes
  useEffect(() => {
    if (isInitialMount.current) {
      fetchConfigs();
      fetchConfigStats();
      isInitialMount.current = false;
    } else if (Object.values(searchFilters).some((filter) => filter !== "")) {
      const debounceTimer = setTimeout(() => {
        fetchConfigs();
      }, 500);
      return () => clearTimeout(debounceTimer);
    } else {
      fetchConfigs();
    }
  }, [currentPage, searchFilters]);

  // Fetch configs function
  const fetchConfigs = async () => {
    try {
      setIsLoadingConfigs(true);
      const params: MCPConfigListParams = {
        skip: (currentPage - 1) * pageSize,
        limit: pageSize,
        ...(searchFilters.mcp_name && {
          mcp_name: searchFilters.mcp_name,
        }),
      };

      const response = await mcpConfigService.getMCPConfigs(params);
      setConfigs(response.data.config || []);
      setTotalCount(response.data.total_count);
    } catch (error) {
      console.error("Error fetching MCP configs:", error);
      toast.error("Failed to load MCP configurations");
    } finally {
      setIsLoadingConfigs(false);
    }
  };

  // Fetch config statistics
  const fetchConfigStats = async () => {
    try {
      setIsLoadingStats(true);
      const configStats = await mcpConfigService.getMCPConfigStats();
      setStats(configStats);
    } catch (error) {
      console.error("Error fetching MCP config stats:", error);
    } finally {
      setIsLoadingStats(false);
    }
  };

  // Create config handler
  const handleCreateConfig = () => {
    setSelectedConfig(null);
    setIsFormOpen(true);
  };

  // Edit config handler
  const handleEditConfig = (config: MCPConfigType) => {
    setSelectedConfig(config);
    setIsFormOpen(true);
  };

  // Close form handler
  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedConfig(null);
  };

  // Form submit handler
  const handleFormSubmit = async (
    data: CreateMCPConfigData | UpdateMCPConfigData
  ) => {
    setIsLoading(true);
    try {
      if (selectedConfig) {
        // Update existing config
        await mcpConfigService.updateMCPConfig(
          selectedConfig._id,
          data as UpdateMCPConfigData
        );
        handleCloseForm();
        toast.success(`Configuration "${data.mcp_name}" updated successfully!`);
        await fetchConfigs();
        await fetchConfigStats();
      } else {
        // Create new config
        await mcpConfigService.createMCPConfig(data as CreateMCPConfigData);
        handleCloseForm();
        toast.success(`Configuration "${data.mcp_name}" created successfully!`);
        await fetchConfigs();
        await fetchConfigStats();
      }
    } catch (error: any) {
      console.error("Error saving MCP config:", error);
      if (error.response?.data?.error?.error_description) {
        toast.error(error.response.data.error.error_description);
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(
          selectedConfig
            ? "Failed to update configuration"
            : "Failed to create configuration"
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Delete confirmation handler
  const handleDeleteClick = (config: MCPConfigType) => {
    setConfigToDelete(config);
    setShowDeleteConfirm(true);
  };

  // Confirm delete handler
  const handleConfirmDelete = async () => {
    if (configToDelete) {
      try {
        await mcpConfigService.deleteMCPConfig(configToDelete._id);
        setConfigToDelete(null);
        setShowDeleteConfirm(false);
        toast.success(
          `Configuration "${configToDelete.mcp_name}" deleted successfully!`
        );
        await fetchConfigs();
        await fetchConfigStats();
      } catch (error: any) {
        console.error("Error deleting MCP config:", error);
        if (error.response?.data?.error?.error_description) {
          toast.error(error.response.data.error.error_description);
        } else if (error.response?.data?.message) {
          toast.error(error.response.data.message);
        } else {
          toast.error("Failed to delete configuration");
        }
      }
    }
  };

  // Cancel delete handler
  const handleCancelDelete = () => {
    setConfigToDelete(null);
    setShowDeleteConfirm(false);
  };

  // Manage tools handler
  const handleManageTools = (config: MCPConfigType) => {
    setSelectedConfigForTools(config);
    setShowToolsModal(true);
  };

  // Close tools modal handler
  const handleCloseToolsModal = () => {
    setShowToolsModal(false);
    setSelectedConfigForTools(null);
  };

  // Sync tools handler
  const handleSyncTools = async (configId: string) => {
    setIsSyncing(true);
    try {
      const updatedConfig = await mcpConfigService.syncMCPConfigTools(configId);
      // Update the config in the list
      setConfigs(prev => prev.map(config =>
        config._id === configId ? updatedConfig : config
      ));
      // Update the selected config for tools if it's the same one
      if (selectedConfigForTools && selectedConfigForTools._id === configId) {
        setSelectedConfigForTools(updatedConfig);
      }
      await fetchConfigStats();
    } catch (error: any) {
      console.error("Error syncing tools:", error);
      throw error; // Re-throw to let the modal handle the error display
    } finally {
      setIsSyncing(false);
    }
  };

  // Activate tools handler
  const handleActivateTools = async (data: ActivateToolsData) => {
    setIsActivatingTools(true);
    try {
      await mcpConfigService.activateTools(data);
      // Refresh the configs to get updated tool states
      await fetchConfigs();
      await fetchConfigStats();
    } catch (error: any) {
      console.error("Error activating tools:", error);
      throw error; // Re-throw to let the modal handle the error display
    } finally {
      setIsActivatingTools(false);
    }
  };

  // Search handler
  const handleSearchChange = (field: string, value: string) => {
    setSearchFilters((prev) => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  // Pagination handler
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Pagination calculations
  const totalPages = Math.ceil(totalCount / pageSize);
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="">
        {/* Header */}
        <div className="sticky top-0 z-40 bg-white border-slate-200 border-b mb-4">
          <div className="px-6 py-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  MCP Configurations
                </h1>
                <p className="text-xs text-slate-600">
                  Manage your Model Context Protocol endpoint configurations
                </p>
              </div>
              <div className="flex gap-3">
                <Can permission="integrations:create">
                <button
                  onClick={handleCreateConfig}
                  disabled={isLoading}
                  type="button"
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="w-4 h-4" />
                  Create Configuration
                </button>
                </Can>
              </div>
            </div>
          </div>
        </div>

        {/* Metric Cards */}
        <MetricCardGrid>
          <MetricCard
            title="Total Configurations"
            value={isLoadingStats ? "..." : stats.total_configs}
            icon={Settings}
            iconColor="text-purple-600"
            iconBgColor="bg-purple-100"
          />
          <MetricCard
            title="Active Configurations"
            value={isLoadingStats ? "..." : stats.active_configs}
            icon={Activity}
            iconColor="text-green-600"
            iconBgColor="bg-green-100"
          />
          <MetricCard
            title="Configs with Tools"
            value={isLoadingStats ? "..." : stats.configs_with_tools}
            icon={Cog}
            iconColor="text-blue-600"
            iconBgColor="bg-blue-100"
          />
        </MetricCardGrid>

        {/* Search Filters */}
        <div className="bg-white rounded-lg border border-gray-200 mb-6 p-6">
          <div className="flex flex-col gap-4">
            {/* Top Section */}
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">
                Search Filters
              </h3>

              <div className="flex items-center gap-2">
                <Tooltip content="Grid view">
                  <button
                    onClick={() => setViewMode("grid")}
                    className={`p-2 rounded-lg transition-colors ${
                      viewMode === "grid"
                        ? "bg-blue-100 text-blue-600"
                        : "text-gray-400 hover:text-gray-600"
                    }`}
                    type="button"
                  >
                    <Grid3X3 className="w-5 h-5" />
                  </button>
                </Tooltip>

                <Tooltip content="List view">
                  <button
                    onClick={() => setViewMode("list")}
                    className={`p-2 rounded-lg transition-colors ${
                      viewMode === "list"
                        ? "bg-blue-100 text-blue-600"
                        : "text-gray-400 hover:text-gray-600"
                    }`}
                    type="button"
                  >
                    <List className="w-5 h-5" />
                  </button>
                </Tooltip>
              </div>
            </div>

            {/* Single Search Field */}
            <div className="relative w-full h-10 flex items-center">
              {/* Search Icon */}
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />

              {/* Input */}
              <input
                type="text"
                placeholder="Search by MCP name..."
                className="pl-10 pr-8 py-2 w-full border border-slate-300 rounded-lg 
                focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                value={searchFilters.mcp_name}
                onChange={(e) => handleSearchChange("mcp_name", e.target.value)}
              />

              {/* Clear Button — appears only when text is typed */}
              {searchFilters.mcp_name && (
                <button
                  type="button"
                  onClick={() => handleSearchChange("mcp_name", "")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 flex items-center justify-center w-6 h-6"
                >
                <Tooltip content="Clear search">
                  <X className="w-4 h-4" />
                </Tooltip>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Configs Content */}
        {isLoadingConfigs ? (
          <div className="flex flex-col items-center justify-center min-h-[400px]">
            <Loader />
          </div>
        ) : configs.length > 0 ? (
          <>
            {viewMode === "grid" ? (
              <GridView
                configs={configs}
                onEdit={handleEditConfig}
                onDelete={handleDeleteClick}
                onManageTools={handleManageTools}
              />
            ) : (
              <ListView
                configs={configs}
                onEdit={handleEditConfig}
                onDelete={handleDeleteClick}
                onManageTools={handleManageTools}
              />
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing <span className="font-medium">{startItem}</span> to{" "}
                  <span className="font-medium">{endItem}</span> of{" "}
                  <span className="font-medium">{totalCount}</span>{" "}
                  configurations
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  {/* Page Numbers */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === pageNum
                            ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                            : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <Terminal className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No configurations found
            </h3>
            <p className="text-gray-500 mb-6">
              {Object.values(searchFilters).some((filter) => filter !== "")
                ? "No configurations match your current search criteria. Try adjusting your filters."
                : "Get started by creating your first MCP configuration."}
            </p>
            <Can permission="integrations:create">
            <button
              onClick={handleCreateConfig}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg flex items-center gap-2 mx-auto transition-colors duration-150"
            >
              <Plus className="w-4 h-4" />
              Create Configuration
            </button>
            </Can>
          </div>
        )}
      </div>

      {/* MCP Config Form Modal */}
      <MCPConfigForm
        isOpen={isFormOpen}
        onClose={handleCloseForm}
        config={selectedConfig}
        onSubmit={handleFormSubmit}
        isLoading={isLoading}
      />

      {/* Confirmation Modal for Delete */}
      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete MCP Configuration"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        description={`Are you sure you want to delete the configuration <strong>${configToDelete?.mcp_name}</strong>? This action cannot be undone.`}
        iconName="warning"
      />

      {/* Tools Management Modal */}
      <ToolsManagementModal
        isOpen={showToolsModal}
        onClose={handleCloseToolsModal}
        config={selectedConfigForTools}
        onSync={handleSyncTools}
        onActivateTools={handleActivateTools}
        isSyncing={isSyncing}
        isActivating={isActivatingTools}
      />
    </div>
  );
};

export default MCPEndpoints;
